// Client-side cold-restart resume-state API.
//
// R2 contract (handoff 2026-10-12 claude-runtime-r2-session-protocols):
//   Mirrors services/api-server/routes/resume-state.mjs. The useResumeOnMount
//   hook (../hooks/useResumeOnMount.ts) calls this on mount and on
//   customer-context change so the UI can rehydrate without re-prompting
//   the rep for prior decisions.

import type { SignedRuntimeApiClient } from './transport.js';
import type { HistoryEntry } from './history.js';
import type { DraftRow } from './drafts.js';

export interface AmbiguousOutcome {
  id: string;
  status: 'open' | 'resolved';
  correlationId: string;
  actionSlug: string;
  vendor: string | null;
  errorCode: string;
  errorMessage: string | null;
  createdAt: string;
  resolvedAt: string | null;
  resolvedTo: 'success' | 'failure' | null;
  resolvedByUserId: string | null;
  resolutionNote: string | null;
  reconciliationEvent: Record<string, unknown> | null;
}

export interface ResumeState {
  projectId: string;
  objective: string | null;
  approvedDecisions: HistoryEntry[];
  outstandingSteps: DraftRow[];
  pendingDrafts: DraftRow[];
  openAmbiguousOutcomes: AmbiguousOutcome[];
}

export class ResumeStateApi {
  constructor(private readonly client: SignedRuntimeApiClient) {}

  async fetch(projectId: string): Promise<ResumeState> {
    return this.client.request<ResumeState>({
      method: 'GET',
      path: `/api/projects/${encodeURIComponent(projectId)}/resume-state`,
    });
  }
}

export function createResumeStateApi(client: SignedRuntimeApiClient): ResumeStateApi {
  return new ResumeStateApi(client);
}
